/**
 * AI Module API Client
 *
 * Provides functions for interacting with AI providers, models, prompts, and tasks.
 */

import apiClient from './client';

// Types
export type ProviderKey = 'openai' | 'openrouter' | 'anthropic' | 'cloudflare';

export interface AIProvider {
  id: string;
  provider_key: ProviderKey;
  api_endpoint?: string;
  is_active: number;
  created_at: string;
  updated_at: string;
}

export interface AIModel {
  id: string;
  model_name: string;
  display_name: string;
  description?: string;
  capabilities?: string;
  max_tokens?: number;
  cost_per_1k_tokens?: number;
  is_active: number;
  is_default: number;
  is_starred: number;
  provider_key?: ProviderKey;
  created_at: string;
  updated_at: string;
}

export interface AIPrompt {
  id: string;
  name: string;
  description?: string;
  category: string;
  system_message?: string;
  template: string;
  variables?: string;
  model_id?: string;
  is_active: number;
  created_at: string;
  updated_at: string;
}

export interface AITask {
  task_key: string;
  display_name: string;
  description?: string;
  category: string;
  is_enabled: boolean;
  usage_count: number;
  system_message?: string;
  template?: string;
}

export interface ExecuteTaskInput {
  variables?: Record<string, string>;
  temperature?: number;
  maxTokens?: number;
  jsonMode?: boolean;
}

export interface ExecuteResult {
  success: boolean;
  content: string;
  parsed?: unknown;
  tokensUsed?: number;
  error?: string;
}

export interface ProviderInput {
  provider_key: ProviderKey;
  api_key: string;
  api_endpoint?: string;
}

export interface PromptInput {
  name: string;
  description?: string;
  category?: string;
  system_message?: string;
  template: string;
  variables?: unknown[];
  model_id?: string;
}

export interface TestConnectionResult {
  success: boolean;
  message?: string;
  error?: string;
}

// Provider names for display
export const PROVIDER_NAMES: Record<ProviderKey, string> = {
  openai: 'OpenAI',
  openrouter: 'OpenRouter',
  anthropic: 'Anthropic',
  cloudflare: 'Cloudflare AI',
};

/**
 * AI API functions
 */
export const aiApi = {
  // Providers
  async listProviders(): Promise<{ items: AIProvider[] }> {
    const { data } = await apiClient.get('/v1/ai/providers');
    return data;
  },

  async addProvider(input: ProviderInput): Promise<{ success: boolean; provider_key: string }> {
    const { data } = await apiClient.post('/v1/ai/providers', input);
    return data;
  },

  async deleteProvider(providerKey: ProviderKey): Promise<{ success: boolean }> {
    const { data } = await apiClient.delete(`/v1/ai/providers/${providerKey}`);
    return data;
  },

  async testProvider(providerKey: ProviderKey): Promise<TestConnectionResult> {
    const { data } = await apiClient.post(`/v1/ai/providers/${providerKey}/test`);
    return data;
  },

  // Models
  async listModels(): Promise<{ items: AIModel[] }> {
    const { data } = await apiClient.get('/v1/ai/models');
    return data;
  },

  async toggleModelStar(modelId: string): Promise<{ success: boolean }> {
    const { data } = await apiClient.put(`/v1/ai/models/${modelId}/star`);
    return data;
  },

  async setDefaultModel(modelId: string): Promise<{ success: boolean }> {
    const { data } = await apiClient.put(`/v1/ai/models/${modelId}/default`);
    return data;
  },

  // Prompts
  async listPrompts(category?: string): Promise<{ items: AIPrompt[] }> {
    const params = category ? { category } : {};
    const { data } = await apiClient.get('/v1/ai/prompts', { params });
    return data;
  },

  async createPrompt(input: PromptInput): Promise<{ id: string; success: boolean }> {
    const { data } = await apiClient.post('/v1/ai/prompts', input);
    return data;
  },

  async updatePrompt(
    id: string,
    input: Partial<PromptInput & { is_active?: boolean }>
  ): Promise<{ success: boolean }> {
    const { data } = await apiClient.put(`/v1/ai/prompts/${id}`, input);
    return data;
  },

  async deletePrompt(id: string): Promise<{ success: boolean }> {
    const { data } = await apiClient.delete(`/v1/ai/prompts/${id}`);
    return data;
  },

  // Tasks
  async listTasks(): Promise<{ items: AITask[] }> {
    const { data } = await apiClient.get('/v1/ai/tasks');
    return data;
  },

  async executeTask(taskKey: string, input: ExecuteTaskInput = {}): Promise<ExecuteResult> {
    const { data } = await apiClient.post(`/v1/ai/tasks/${taskKey}/execute`, input);
    return data;
  },

  // Direct execution (custom prompt)
  async execute(input: {
    prompt: string;
    systemMessage?: string;
    provider?: ProviderKey;
    model?: string;
    temperature?: number;
    maxTokens?: number;
    jsonMode?: boolean;
  }): Promise<{ success: boolean; content: string }> {
    const { data } = await apiClient.post('/v1/ai/execute', input);
    return data;
  },
};

export default aiApi;
